import React, { useState } from "react";
import axios from "axios";
import Header from "../components/Header";
import Footer from "../components/Footer";
import PageBanner from "../components/PageBanner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { MapPin, Phone, Globe } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    phone: "",
    email: "",
    message: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.first_name || !formData.email || !formData.message) {
      toast.error("Please fill all required fields");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success("Message sent successfully! We'll get back to you soon.");
      setFormData({
        first_name: "",
        last_name: "",
        phone: "",
        email: "",
        message: ""
      });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="contact-page">
      <Header />
      
      <PageBanner
        title="CONTACT US"
        breadcrumbs={[{ name: "CONTACT US" }]}
        backgroundImage="/assets/contact_us_banner.png"
      />

      {/* Contact Section - Enhanced */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-gradient-to-r from-[#FEF3E2] to-[#FFE5CC] rounded-full mb-4">
              <div className="w-2 h-2 bg-[#E67E22] rounded-full animate-pulse"></div>
              <span className="text-[#E67E22] text-sm font-bold tracking-widest">LET'S CONNECT</span>
            </div>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#2C3E50] mb-4">
              Get In <span className="gradient-text">Touch</span>
            </h2>
            <p className="text-[#7F8C8D] text-lg max-w-2xl mx-auto">
              Reach out to us anytime for inquiries, reservations, or assistance. We're here to make your WyldWoods experience seamless and memorable.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-12 items-start">
            {/* Contact Image */}
            <div className="relative animate-slide-in-left">
              <div className="absolute -inset-4 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-3xl opacity-20 blur-2xl"></div>
              <div className="relative image-hover rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://images.pexels.com/photos/3184360/pexels-photo-3184360.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Contact Us"
                  className="w-full h-[500px] object-cover"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div className="premium-card p-8 animate-slide-in-right">
              <h3 className="font-serif text-3xl font-bold text-[#2C3E50] mb-2">
                Send Us A Message
              </h3>
              <p className="text-[#7F8C8D] mb-8">
                Contact us today, and let's make your stay extraordinary!
              </p>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="first_name" className="text-[#2C3E50] font-semibold">First Name *</Label>
                    <Input
                      id="first_name"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleChange}
                      placeholder="John"
                      required
                      className="bg-white border-2 border-gray-200 focus:border-[#E67E22] rounded-lg h-12"
                      data-testid="first-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="last_name" className="text-[#2C3E50] font-semibold">Last Name</Label>
                    <Input
                      id="last_name"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleChange}
                      placeholder="Doe"
                      className="bg-white border-2 border-gray-200 focus:border-[#E67E22] rounded-lg h-12"
                      data-testid="last-name-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone" className="text-[#2C3E50] font-semibold">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="+91 XXXXX XXXXX"
                      className="bg-white border-2 border-gray-200 focus:border-[#E67E22] rounded-lg h-12"
                      data-testid="phone-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email" className="text-[#2C3E50] font-semibold">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="john@example.com"
                      required
                      className="bg-white border-2 border-gray-200 focus:border-[#E67E22] rounded-lg h-12"
                      data-testid="email-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="message" className="text-[#2C3E50] font-semibold">Message *</Label>
                    <span className={`text-sm ${formData.message.length >= 200 ? 'text-red-500 font-semibold' : 'text-gray-500'}`}>
                      {formData.message.length}/200
                    </span>
                  </div>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Tell us about your stay preferences or any questions you have..."
                    rows={5}
                    required
                    maxLength={200}
                    className="bg-white border-2 border-gray-200 focus:border-[#E67E22] rounded-lg resize-none"
                    data-testid="message-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full btn-gradient text-lg py-6"
                  data-testid="submit-btn"
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Sending Message...
                    </span>
                  ) : "Send Message"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Info Cards - Enhanced */}
      <section className="py-16 px-4 bg-gradient-to-b from-white to-[#FDFBF7]">
        <div className="max-w-6xl mx-auto">
          <div className="grid md:grid-cols-3 gap-8">
            {/* Address Card */}
            <div className="premium-card p-8 card-hover group animate-fade-in-up" data-testid="address-card">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <MapPin className="text-white" size={28} />
                </div>
                <h4 className="font-serif text-xl font-bold text-[#2C3E50]">Our Address</h4>
                <div className="w-16 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                <p className="text-[#7F8C8D] leading-relaxed">
                  WyldWoods Rural Retreat<br />
                  61 Puthiamman Street<br />
                  Panaiyur East Coast Road<br />
                  Edaikazhinadu – 603302
                </p>
              </div>
            </div>

            {/* Phone Card */}
            <div className="premium-card p-8 card-hover group animate-fade-in-up delay-100" data-testid="phone-card">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <Phone className="text-white" size={28} />
                </div>
                <h4 className="font-serif text-xl font-bold text-[#2C3E50]">Call Us</h4>
                <div className="w-16 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                <a href="tel:+919445771282" className="text-[#7F8C8D] hover:text-[#E67E22] transition-colors text-lg font-semibold">
                  +91 9445771282
                </a>
                <p className="text-sm text-gray-500">Available 24/7</p>
              </div>
            </div>

            {/* Web Card */}
            <div className="premium-card p-8 card-hover group animate-fade-in-up delay-200" data-testid="web-card">
              <div className="flex flex-col items-center text-center space-y-4">
                <div className="w-16 h-16 bg-gradient-to-br from-[#E67E22] to-[#F39C12] rounded-2xl flex items-center justify-center shadow-lg group-hover:scale-110 group-hover:rotate-12 transition-all duration-300">
                  <Globe className="text-white" size={28} />
                </div>
                <h4 className="font-serif text-xl font-bold text-[#2C3E50]">Online</h4>
                <div className="w-16 h-1 bg-gradient-to-r from-[#E67E22] to-[#F39C12] rounded-full"></div>
                <div className="space-y-2">
                  <a href="mailto:support@wyldwoods.in" className="block text-[#E67E22] hover:text-[#D35400] transition-colors font-semibold">
                    support@wyldwoods.in
                  </a>
                  <a 
                    href="https://maps.app.goo.gl/R25yuvWPXKouUso2A" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-block btn-secondary mt-4 text-sm py-2 px-6"
                  >
                    View on Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
